import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { Table, TableBody, TableRow } from '@mui/material'
import { EmployerBenefitProgram } from 'Utils/types/benefitProgram'
import { FlexColumn, spacingMD } from 'Utils/styles/spacing'
import { TextSpan } from 'Components/TextSpan'
import { fontSizeLG, fontWeightBold } from 'Utils/styles/text'
import { SortableTableHeader, TableHeaderCell } from 'Components/Table/SortableTableHeader'
import { StyledPaper, StyledTableContainer } from 'Components/Table/StyledTableComponents'
import { StyledTableCell } from 'Components/Table/StyledTableCell'
import { CurrencyText } from 'Components/Text/CurrencyText'
import { useTableOrder } from 'Hooks/useTableOrder'
import { sortTableRows } from 'Utils/helpers/tables'
import { useGetRecurringAllowancesQuery } from 'Hooks/api/JOONAdmin/useGetRecurringAllowancesQuery'
import {
  convertRecurringAllowanceToTableRow,
  RecurringAllowanceRow
} from 'Utils/helpers/recurringAllowance'

type ProgramRecurringAllowancesProps = {
  program: EmployerBenefitProgram
}

const Container = styled(FlexColumn)`
  padding: ${spacingMD};
`

const headerCells: readonly TableHeaderCell<RecurringAllowanceRow>[] = [
  { id: 'user', numeric: false, label: 'User' },
  { id: 'allowanceType', numeric: false, label: 'Type' },
  { id: 'amount', numeric: true, label: 'Amount' },
  { id: 'cadence', numeric: false, label: 'Cadence' },
  { id: 'duration', numeric: false, label: 'Duration' },
  { id: 'startDate', numeric: false, label: 'Start Date' },
  { id: 'nextOccurrence', numeric: false, label: 'Next Occurrence' },
  { id: 'active', numeric: false, label: 'Active' },
  { id: 'notes', numeric: false, label: 'Notes' }
] as const

export const ProgramRecurringAllowances: FC<ProgramRecurringAllowancesProps> = ({ program }) => {
  const { order, orderBy, handleRequestSort } = useTableOrder<RecurringAllowanceRow>({
    defaultOrderBy: 'nextOccurrence',
    defaultOrder: 'asc'
  })
  const { data, isLoading } = useGetRecurringAllowancesQuery({
    benefitProgramId: program.id,
    pageNumber: 1,
    perPage: 1000
  })

  const tableRows = useMemo(
    () => (data?.recurringAllowances || []).map(convertRecurringAllowanceToTableRow),
    [data?.recurringAllowances]
  )
  const sortedRows = useMemo(
    () => sortTableRows(tableRows, order, orderBy),
    [tableRows, order, orderBy]
  )

  return (
    <Container>
      <TextSpan fontSize={fontSizeLG} fontWeight={fontWeightBold}>
        Recurring Allowances
        {data?.recurringAllowances ? ` (${data.recurringAllowances.length})` : ''}
      </TextSpan>
      {isLoading ? (
        <TextSpan>Loading...</TextSpan>
      ) : (data?.recurringAllowances || []).length === 0 ? (
        <TextSpan>No recurring allowances found for this program.</TextSpan>
      ) : (
        <StyledPaper>
          <StyledTableContainer>
            <Table>
              <SortableTableHeader
                headerCells={headerCells}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                hideActionColumn
              />
              <TableBody>
                {sortedRows.map((row) => (
                  <TableRow key={row.id}>
                    <StyledTableCell>
                      <div>
                        {row.user?.firstName} {row.user?.lastName}
                      </div>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>{row.user?.email}</div>
                    </StyledTableCell>
                    <StyledTableCell>{row.allowanceType}</StyledTableCell>
                    <StyledTableCell>
                      <CurrencyText value={row.amount} currencyCode={row.currencyCode} />
                    </StyledTableCell>
                    <StyledTableCell>{row.cadence}</StyledTableCell>
                    <StyledTableCell>{row.duration}</StyledTableCell>
                    <StyledTableCell>{row.startDate}</StyledTableCell>
                    <StyledTableCell>{row.nextOccurrence}</StyledTableCell>
                    <StyledTableCell>{row.active ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>{row.notes}</StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </StyledPaper>
      )}
    </Container>
  )
}
